import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Award, Mail, Lock, Loader2, MailOpen, AlertTriangle } from "lucide-react";
import { SkDatePicker } from "./SkDatePicker";
import { useSkillCatalog, SkillChips } from "./SkillCatalog";
import { TL } from "../content/talentI18n";
import LevelUpRequest from "./LevelUpRequest";
import RegisteredSeal from "./RegisteredSeal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const box = "border border-white/10 bg-[#0a0a0a] p-6 mb-6";
const inp = "w-full bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none";

function Availability({ L, profile, auth, onSaved }) {
  const [date, setDate] = useState(profile.available_from || "");
  const [city, setCity] = useState(profile.city || "");
  const [msg, setMsg] = useState("");
  const save = async (extra = {}) => {
    try {
      await axios.put(`${API}/applicant/profile`, { available_from: date, city, ...extra }, auth());
      setMsg(L.saved); onSaved();
    } catch (e) { setMsg(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className={box} data-testid="profile-availability">
      <h3 className="font-display text-xl flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-[#D4AF37]" /> {L.availTitle}</h3>
      <p className="text-white/40 text-sm mb-4">{L.availHint}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <SkDatePicker label={L.availTitle} value={date} onChange={setDate} testid="profile-available-from" />
        <div>
          <label className="block text-xs text-white/50 mb-2">{L.city}</label>
          <input value={city} onChange={(e) => setCity(e.target.value)} className={inp} data-testid="profile-city" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button onClick={() => save()} data-testid="profile-save" className="bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#F2E3C6] transition-colors">{L.save}</button>
        {date && <button onClick={() => { setDate(""); save({ available_from: "" }); }} data-testid="profile-not-looking" className="border border-white/15 px-4 py-2.5 text-sm text-white/60 hover:text-white">{L.availNone}</button>}
        {msg && <span className="text-[#D4AF37] text-sm" data-testid="profile-msg">{msg}</span>}
      </div>
    </div>
  );
}

function Skills({ L, profile, labels }) {
  const h = profile.hof;
  return (
    <div className={box} data-testid="profile-skills">
      <h3 className="font-display text-xl flex items-center gap-2 mb-3"><Award size={18} className="text-[#D4AF37]" /> {L.skillsTitle}</h3>
      {h ? (
        <>
          <p className="text-sm text-white/70 mb-3">{h.kind === "certificate" ? L.docCert : L.docVer}: <span className="text-[#D4AF37] font-mono">{h.certificate_no}</span></p>
          {h.skills.length ? <SkillChips ids={h.skills} labels={labels} /> : <p className="text-white/40 text-sm">—</p>}
          {h.skills_other && <p className="text-white/50 text-xs mt-3">{h.skills_other}</p>}
        </>
      ) : <p className="text-white/40 text-sm">{L.skillsNone}</p>}
      <p className="text-white/35 text-xs mt-4">{L.searchNote}</p>
    </div>
  );
}

function Messages({ L, auth, onRead }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { axios.get(`${API}/applicant/messages`, auth()).then(({ data }) => setItems(data)).catch(() => setItems([])); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = async (m) => {
    setOpen(open === m.id ? null : m.id);
    if (!m.read) {
      await axios.post(`${API}/applicant/messages/${m.id}/read`, {}, auth()).catch(() => {});
      setItems((l) => l.map((x) => x.id === m.id ? { ...x, read: true } : x)); onRead();
    }
  };
  return (
    <div className={box} data-testid="profile-messages">
      <h3 className="font-display text-xl flex items-center gap-2 mb-4"><Mail size={18} className="text-[#D4AF37]" /> {L.msgsTitle}</h3>
      {items === null ? <Loader2 size={16} className="animate-spin text-white/40" /> : items.length === 0 ? <p className="text-white/40 text-sm">{L.msgsNone}</p> : (
        <div className="space-y-2">
          {items.map((m) => (
            <button key={m.id} onClick={() => toggle(m)} data-testid={`profile-message-${m.id}`} className={`w-full text-left border px-4 py-3 transition-colors ${m.read ? "border-white/10" : "border-[#D4AF37]/50 bg-[#D4AF37]/5"}`}>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-white truncate flex items-center gap-2">{m.read ? <MailOpen size={14} className="text-white/40" /> : <Mail size={14} className="text-[#D4AF37]" />}{m.subject}</p>
                <span className="text-white/35 text-xs shrink-0">{new Date(m.created_at).toLocaleDateString("sk-SK")}</span>
              </div>
              <p className="text-white/45 text-xs mt-1">{L.from}: {m.from_name} · {m.from_email}</p>
              {open === m.id && <p className="text-white/75 text-sm mt-3 whitespace-pre-wrap">{m.text}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ChangePassword({ L, auth, onDone }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/applicant/change-password`, { current_password: cur, new_password: nw }, auth()); setMsg(L.pwDone); setCur(""); setNw(""); onDone && onDone(); }
    catch (err) { setMsg(err?.response?.data?.detail || "Error"); }
  };
  return (
    <form onSubmit={submit} className={box} data-testid="change-password">
      <h3 className="font-display text-xl flex items-center gap-2 mb-4"><Lock size={18} className="text-[#D4AF37]" /> {L.pwTitle}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder={L.pwCur} className={inp} data-testid="pw-current" required />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder={L.pwNew} className={inp} data-testid="pw-new" required minLength={8} />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button type="submit" data-testid="pw-submit" className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-sm hover:bg-[#D4AF37] hover:text-black transition-colors">{L.pwBtn}</button>
        {msg && <span className="text-[#D4AF37] text-sm" data-testid="pw-msg">{msg}</span>}
      </div>
    </form>
  );
}

export default function MyProfile({ lang, auth, onUnread, children }) {
  const L = TL[lang] || TL.sk;
  const { labels } = useSkillCatalog(lang);
  const [profile, setProfile] = useState(null);
  const load = useCallback(() => axios.get(`${API}/applicant/profile`, auth()).then(({ data }) => { setProfile(data); onUnread && onUnread(data.unread); }).catch(() => {}), // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  useEffect(() => { load(); }, [load]);
  if (!profile) return <Loader2 className="animate-spin text-white/40" />;
  return (
    <div data-testid="my-profile">
      {profile.hof_claim_pending && <p className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[#F2E3C6] text-sm p-4 mb-6 flex gap-2" data-testid="profile-claim-pending"><AlertTriangle size={16} className="shrink-0 mt-0.5 text-[#D4AF37]" />{L.claimPending}</p>}
      {profile.must_change_password && <p className="border border-red-400/40 bg-red-500/5 text-red-200 text-sm p-4 mb-6" data-testid="profile-must-change">{L.mustChange}</p>}
      <Messages L={L} auth={auth} onRead={load} />
      {profile.hof_cert_no && !profile.hof_claim_pending && <RegisteredSeal lang={lang} />}
      <Skills L={L} profile={profile} labels={labels} />
      <Availability L={L} profile={profile} auth={auth} onSaved={load} />
      <LevelUpRequest lang={lang} auth={auth} />
      <ChangePassword L={L} auth={auth} onDone={load} />
      {children}
    </div>
  );
}
